import { ChevronDown, CircleAlert, Eye, EyeOff, Search, X } from 'lucide-react';
import {
  useId,
  useState,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';

import { cn } from '../../lib/cn.js';
import { SpringCheck } from './SpringCheck.js';

/**
 * A casca de todo campo (design.md §8).
 *
 * Preenchido e sublinhado, como o exemplo de `outline-hidden` da documentação
 * do Tailwind: fundo afundado, sem contorno dos lados, um filete de 2px
 * embaixo que vira laranja no foco — e nada de contorno do navegador. O campo
 * não compete com o cartão branco em que está; é um rebaixo nele, e o
 * sublinhado é o que diz "escreve-se aqui". Cantos retos, de propósito: o raio
 * é do botão e do cartão, e um rebaixo tem canto vivo — é o que separa, à
 * primeira vista, o que se preenche do que se clica. Sob o ponteiro o fundo
 * desce um tom. O erro troca sublinhado e fundo para `danger`.
 *
 * Sem ícone à esquerda: o rótulo em cima já diz o que é o campo, e o ícone
 * repetia o rótulo em desenho. A exceção é a busca, que não tem rótulo à
 * vista — a lupa é o rótulo dela, e acende em laranja junto com o sublinhado
 * (o `group` do invólucro). Ícone que age fica: o olho da senha, o "×" que
 * limpa, o chevron do seletor.
 *
 * As constantes são exportadas porque campo não é só `<input>`: o gatilho do
 * seletor, a busca da barra de filtros, o campo somente-leitura do link gerado
 * e o nome da coluna nova do quadro vestem a mesma casca, e é isso que os faz
 * parecer um só sistema.
 */
const CONTROL_SHELL =
  'w-full rounded-none border-b-2 bg-sunken text-ink transition-[border-color,background-color] duration-100 hover:bg-sunken-2 focus:outline-hidden disabled:cursor-not-allowed disabled:opacity-55 disabled:hover:bg-sunken';

/** O controle, sem o respiro do ícone. */
export const CONTROL_BASE = `${CONTROL_SHELL} px-3 placeholder:text-ink-3`;

/** Respiro à esquerda para a lupa da busca. */
const CONTROL_WITH_ICON = 'pl-9';

export const CONTROL_TONE = {
  rest: 'border-line-2 focus:border-primary',
  invalid: 'border-danger bg-danger-soft hover:bg-danger-soft focus:border-danger',
} as const;

/** Para gatilho que é `<button>`: o sublinhado laranja só aparece com o teclado. */
export const CONTROL_BUTTON = 'focus-visible:border-primary focus-visible:outline-hidden';

/** Campo em estado aberto (seletor com o painel à mostra): o mesmo sinal do foco. */
export const CONTROL_OPEN = 'border-primary';

/** A lupa da busca: laranja enquanto o campo tem foco, pelo `group-focus-within`. */
function SearchIcon() {
  return (
    <Search
      size={16}
      strokeWidth={1.5}
      className="pointer-events-none absolute top-1/2 left-3 z-10 -translate-y-1/2 text-ink-3 transition-colors duration-100 group-focus-within:text-primary"
      aria-hidden
    />
  );
}

interface FieldShellProps {
  readonly label: string;
  readonly hint?: string | undefined;
  readonly error?: string | undefined;
  readonly required?: boolean | undefined;
  readonly children: (props: {
    id: string;
    describedBy: string | undefined;
    invalid: boolean;
  }) => ReactNode;
}

/**
 * Casca comum de campo: rótulo associado por `for`/`id`, texto de ajuda e erro
 * anunciados por `aria-describedby` — design.md §13.
 */
export function Field({ label, hint, error, required, children }: FieldShellProps) {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const describedBy = [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(' ');

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-[13px] font-medium text-ink">
        {label}
        {required ? <span className="ml-0.5 text-primary">*</span> : null}
      </label>

      <div className="group relative">
        {children({ id, describedBy: describedBy || undefined, invalid: Boolean(error) })}
      </div>

      {hint && !error ? (
        <p id={hintId} className="text-[13px] text-ink-3">
          {hint}
        </p>
      ) : null}

      {error ? (
        <p id={errorId} className="flex items-center gap-1.5 text-[13px] text-danger" role="alert">
          <CircleAlert size={14} strokeWidth={1.75} className="shrink-0" aria-hidden />
          {error}
        </p>
      ) : null}
    </div>
  );
}

type TextFieldProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> & {
  readonly label: string;
  readonly hint?: string | undefined;
  readonly error?: string | undefined;
  /** Aplica a fonte monoespaçada — para valor, data, nº de contrato. */
  readonly mono?: boolean | undefined;
};

export function TextField({ label, hint, error, mono, className, ...rest }: TextFieldProps) {
  return (
    <Field
      label={label}
      {...(hint ? { hint } : {})}
      {...(error ? { error } : {})}
      required={rest.required === true}
    >
      {({ id, describedBy, invalid }) => (
        <input
          id={id}
          aria-describedby={describedBy}
          aria-invalid={invalid}
          className={cn(
            CONTROL_BASE,
            'h-10 text-sm',
            mono && 'font-mono',
            invalid ? CONTROL_TONE.invalid : CONTROL_TONE.rest,
            className,
          )}
          {...rest}
        />
      )}
    </Field>
  );
}

type PasswordFieldProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'id' | 'type'> & {
  readonly label: string;
  readonly hint?: string | undefined;
  readonly error?: string | undefined;
};

/**
 * Campo de senha com o olho para conferir o que se digitou.
 *
 * Senha em pontinhos é proteção contra quem olha por cima do ombro — e quem
 * troca a própria senha costuma estar sozinho, errando a digitação de uma frase
 * longa e sem saber onde. O olho devolve essa checagem sem baixar o padrão: o
 * campo nasce oculto, e revelar é ação deliberada de quem está ali.
 *
 * Só aparece quando há o que revelar. Num campo vazio seria enfeite, e enfeite
 * que parece botão é convite a um clique que não faz nada.
 */
export function PasswordField({ label, hint, error, className, ...rest }: PasswordFieldProps) {
  const [visivel, setVisivel] = useState(false);
  const temValor = rest.value === undefined || String(rest.value) !== '';

  return (
    <Field
      label={label}
      {...(hint ? { hint } : {})}
      {...(error ? { error } : {})}
      required={rest.required === true}
    >
      {({ id, describedBy, invalid }) => (
        <>
          <input
            id={id}
            type={visivel ? 'text' : 'password'}
            aria-describedby={describedBy}
            aria-invalid={invalid}
            className={cn(
              CONTROL_BASE,
              'h-10 pr-10 text-sm',
              // O Edge põe um olho próprio, e dois olhos no mesmo campo confundem.
              '[&::-ms-reveal]:hidden',
              invalid ? CONTROL_TONE.invalid : CONTROL_TONE.rest,
              className,
            )}
            {...rest}
          />

          {temValor ? (
            <button
              type="button"
              onClick={() => setVisivel((antes) => !antes)}
              aria-label={visivel ? 'Ocultar a senha' : 'Mostrar a senha'}
              aria-pressed={visivel}
              aria-controls={id}
              className="absolute inset-y-0 right-0 z-10 grid w-10 place-items-center text-ink-3 transition-colors duration-100 hover:text-ink"
            >
              {visivel ? (
                <EyeOff size={16} strokeWidth={1.5} aria-hidden />
              ) : (
                <Eye size={16} strokeWidth={1.5} aria-hidden />
              )}
            </button>
          ) : null}
        </>
      )}
    </Field>
  );
}

type TextAreaFieldProps = Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'id'> & {
  readonly label: string;
  readonly hint?: string | undefined;
  readonly error?: string | undefined;
};

export function TextAreaField({ label, hint, error, className, ...rest }: TextAreaFieldProps) {
  return (
    <Field
      label={label}
      {...(hint ? { hint } : {})}
      {...(error ? { error } : {})}
      required={rest.required === true}
    >
      {({ id, describedBy, invalid }) => (
        <textarea
          id={id}
          aria-describedby={describedBy}
          aria-invalid={invalid}
          rows={rest.rows ?? 4}
          className={cn(
            CONTROL_BASE,
            'resize-y py-2.5 text-sm leading-relaxed',
            invalid ? CONTROL_TONE.invalid : CONTROL_TONE.rest,
            className,
          )}
          {...rest}
        />
      )}
    </Field>
  );
}

type SelectFieldProps = Omit<SelectHTMLAttributes<HTMLSelectElement>, 'id'> & {
  readonly label: string;
  readonly hint?: string | undefined;
  readonly error?: string | undefined;
  readonly children: ReactNode;
};

export function SelectField({
  label,
  hint,
  error,
  className,
  children,
  ...rest
}: SelectFieldProps) {
  return (
    <Field
      label={label}
      {...(hint ? { hint } : {})}
      {...(error ? { error } : {})}
      required={rest.required === true}
    >
      {({ id, describedBy, invalid }) => (
        /*
         * A seta é nossa, não a do navegador.
         *
         * Cada sistema desenha a sua num tamanho e num alinhamento diferentes,
         * e nenhuma delas conversava com o resto. Com `appearance-none` o
         * campo fica limpo e o ícone é posicionado por nós, idêntico ao do
         * seletor com busca.
         */
        <>
          <select
            id={id}
            aria-describedby={describedBy}
            aria-invalid={invalid}
            className={cn(
              CONTROL_BASE,
              'h-10 cursor-pointer appearance-none pr-9 text-sm',
              invalid ? CONTROL_TONE.invalid : CONTROL_TONE.rest,
              className,
            )}
            {...rest}
          >
            {children}
          </select>

          <ChevronDown
            size={15}
            strokeWidth={1.5}
            className="pointer-events-none absolute top-1/2 right-3 -translate-y-1/2 text-ink-3"
            aria-hidden
          />
        </>
      )}
    </Field>
  );
}

type CheckboxProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'id' | 'type'> & {
  readonly label: string;
  readonly hint?: string | undefined;
};

/** O anel de foco e o esmaecido do desenho seguem o estado do `<input>` ao lado. */
const CHECK_VISUAL =
  'peer-focus-visible:ring-[3px] peer-focus-visible:ring-primary/30 peer-disabled:opacity-55';

/**
 * Caixa de seleção do sistema.
 *
 * O `<input>` nativo continua sendo o controle — teclado, leitor de tela e
 * formulário falam com ele —, mas fica invisível por cima do desenho: a caixa
 * com mola (`SpringCheck`), que preenche de laranja e desenha o visto. O
 * estado chega ao desenho por `checked`; sem `checked` (uso não controlado),
 * o componente acompanha o que o input reporta.
 */
export function Checkbox({
  label,
  hint,
  className,
  checked,
  defaultChecked,
  onChange,
  ...rest
}: CheckboxProps) {
  const id = useId();
  const [interno, setInterno] = useState(defaultChecked ?? false);
  const marcada = checked ?? interno;

  return (
    <div className="flex items-start gap-2.5">
      <span className="relative mt-px grid size-[18px] shrink-0 place-items-center">
        <input
          id={id}
          type="checkbox"
          checked={checked}
          defaultChecked={defaultChecked}
          onChange={(event) => {
            if (checked === undefined) setInterno(event.target.checked);
            onChange?.(event);
          }}
          className={cn(
            'peer absolute inset-0 z-10 size-full cursor-pointer appearance-none opacity-0 disabled:cursor-not-allowed',
            className,
          )}
          {...rest}
        />
        <SpringCheck checked={marcada} className={CHECK_VISUAL} />
      </span>

      {/* Desabilitado, o rótulo perde a mãozinha: prometer clique que não vem é pior que não prometer. */}
      <label
        htmlFor={id}
        className={cn(
          'text-sm leading-[18px]',
          rest.disabled ? 'cursor-not-allowed text-ink-3' : 'cursor-pointer',
        )}
      >
        {label}
        {hint ? <span className="mt-0.5 block text-[13px] text-ink-3">{hint}</span> : null}
      </label>
    </div>
  );
}

interface RadioOption<T extends string> {
  readonly value: T;
  readonly label: string;
  readonly hint?: string | undefined;
}

/**
 * Botões de opção do sistema: uma escolha entre poucas, todas à vista.
 *
 * Mesmo desenho da caixa de seleção, redondo: a mola preenche de laranja e
 * faz crescer o ponto em `on-primary`. O `<input>` nativo continua sendo o
 * controle — as setas do teclado trocam a opção dentro do grupo, pelo `name`
 * compartilhado — e o `fieldset` dá ao leitor de tela o nome do grupo.
 */
export function RadioGroup<T extends string>({
  label,
  value,
  options,
  onChange,
  disabled = false,
}: {
  readonly label: string;
  readonly value: T;
  readonly options: readonly RadioOption<T>[];
  readonly onChange: (value: T) => void;
  readonly disabled?: boolean | undefined;
}) {
  const name = useId();

  return (
    <fieldset className="flex flex-col gap-2.5" disabled={disabled}>
      <legend className="sr-only">{label}</legend>

      {options.map((option) => {
        const id = `${name}-${option.value}`;
        const escolhida = value === option.value;

        return (
          <div key={option.value} className="flex items-start gap-2.5">
            <span className="relative mt-px grid size-[18px] shrink-0 place-items-center">
              <input
                id={id}
                type="radio"
                name={name}
                value={option.value}
                checked={escolhida}
                disabled={disabled}
                onChange={() => onChange(option.value)}
                className="peer absolute inset-0 z-10 size-full cursor-pointer appearance-none opacity-0 disabled:cursor-not-allowed"
              />
              <SpringCheck checked={escolhida} shape="round" className={CHECK_VISUAL} />
            </span>

            <label
              htmlFor={id}
              className={cn(
                'text-sm leading-[18px]',
                disabled ? 'cursor-not-allowed text-ink-3' : 'cursor-pointer',
              )}
            >
              {option.label}
              {option.hint ? (
                <span className="mt-0.5 block text-[13px] text-ink-3">{option.hint}</span>
              ) : null}
            </label>
          </div>
        );
      })}
    </fieldset>
  );
}

type SearchInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> & {
  /** Com `onClear`, aparece o "×" para esvaziar o campo quando há texto. */
  readonly onClear?: (() => void) | undefined;
};

/**
 * Campo de busca sem label visível — usado nas barras de filtro, onde a lupa
 * e o placeholder já dizem o que é. Mesma casca dos campos de formulário: a
 * lupa à esquerda acende com o foco, e o "×" à direita esvazia.
 */
export function SearchInput({ className, onClear, value, ...rest }: SearchInputProps) {
  const temTexto = value !== undefined && value !== null && String(value) !== '';
  const limpavel = onClear !== undefined && temTexto;

  return (
    <div className="group relative">
      <SearchIcon />
      <input
        type="search"
        value={value}
        className={cn(
          CONTROL_BASE,
          CONTROL_WITH_ICON,
          'h-10 text-sm',
          limpavel ? 'pr-9' : 'pr-3',
          CONTROL_TONE.rest,
          className,
        )}
        {...rest}
      />
      {limpavel ? (
        <button
          type="button"
          onClick={onClear}
          aria-label="Limpar busca"
          className="absolute top-1/2 right-1.5 grid size-7 -translate-y-1/2 place-items-center text-ink-3 transition-colors hover:bg-sunken-2 hover:text-ink"
        >
          <X size={14} strokeWidth={2} aria-hidden />
        </button>
      ) : null}
    </div>
  );
}
